import { formatMoney, type PaymentImpactDto } from '@bqcrm/shared';
import { Alert, Button, Group, Stack, Text } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api';
import { MoneyInput } from './Money';

/** Skutek wpłaty do umowy (ile zostało na umowie i w sprawie, czy rozliczenie zamknie sprawę) — bez uprawnień zapytanie się nie uda i ostrzeżeń nie ma. */
export function usePaymentImpact(contractId: string) {
  return useQuery({ queryKey: ['payment-impact', contractId], queryFn: () => api<PaymentImpactDto>(`/contracts/${contractId}/payment-impact`), retry: false, staleTime: 0 });
}

/** Czy wpłata rozliczy całą umowę albo zamknie sprawę (QA qa10311123 3.1). */
export function paymentEffect(amountGr: number | null, impact: PaymentImpactDto | undefined): 'sprawa' | 'umowa' | null {
  if (!amountGr || amountGr <= 0 || !impact) return null;
  if (impact.closesWhenPaid && impact.caseOpenGr > 0 && amountGr >= impact.caseOpenGr) return 'sprawa';
  if (impact.contractOpenGr > 0 && amountGr >= impact.contractOpenGr) return 'umowa';
  return null;
}

/**
 * Kwota wpłaty (QA qa10311123 3.1): pole puste na start — pełną kwotę wpisuje się świadomie przyciskiem „Cała kwota”.
 * Pod polem: ile zostanie do zapłaty po wpłacie, a gdy wpłata rozliczy umowę albo zamknie sprawę — ostrzeżenie.
 */
export function PaymentAmountField({
  amountGr,
  onChangeGr,
  remainingGr,
  what,
  impact,
}: {
  amountGr: number | null;
  onChangeGr: (gr: number | null) => void;
  /** pozostało do zapłaty na fakturze / pozycji (null — nieznane, np. wpłata bez przypisania) */
  remainingGr: number | null;
  what: 'faktura' | 'pozycja';
  impact: PaymentImpactDto | undefined;
}) {
  const left = remainingGr !== null && amountGr ? remainingGr - amountGr : null;
  const effect = paymentEffect(amountGr, impact);
  return (
    <Stack gap={6}>
      <Group align="flex-end" gap="xs" wrap="nowrap">
        <MoneyInput label="Kwota brutto" placeholder="kwota z wyciągu" valueGr={amountGr} onChangeGr={onChangeGr} style={{ flex: 1 }} data-autofocus />
        {remainingGr !== null && remainingGr > 0 && (
          <Button variant="light" onClick={() => onChangeGr(remainingGr)}>
            Cała kwota ({formatMoney(remainingGr)})
          </Button>
        )}
      </Group>
      {left !== null && (
        <Text size="sm" c={left < 0 ? 'red' : left === 0 ? 'green.8' : undefined}>
          {left < 0
            ? `Kwota większa niż pozostało do zapłaty (${formatMoney(remainingGr!)})`
            : left === 0
              ? what === 'faktura'
                ? 'Faktura zostanie w pełni opłacona.'
                : 'Pozycja zostanie w pełni opłacona.'
              : `Po tej wpłacie pozostanie: ${formatMoney(left)}`}
        </Text>
      )}
      {effect && impact && (
        <Alert color="orange" variant="light" icon={<IconAlertTriangle size={18} />} p="xs">
          <Text size="sm">
            {effect === 'sprawa' ? (
              <>
                Ta wpłata <b>zamknie sprawę {impact.caseNumber}</b>: wszystkie płatności będą rozliczone, a pozostałe warunki zamknięcia są spełnione. Klient może dostać e-mail o zakończeniu realizacji.
              </>
            ) : (
              <>
                Ta wpłata <b>rozliczy całą umowę{impact.contractNumber ? ` ${impact.contractNumber}` : ''}</b>.
              </>
            )}{' '}
            Sprawdź kwotę z wyciągiem — wpłatę częściową wpisz w takiej wysokości, w jakiej wpłynęła.
          </Text>
        </Alert>
      )}
    </Stack>
  );
}
